(() => {
  const ROOT_ATTR = 'data-smh';
  const HUB_READY = 'data-smh-ready';
  const MOTION_READY = 'data-smh-motion';
  const RETRY_MS = 80;
  const RETRY_MAX = 40;
  const COLS = 52;
  const ROWS = 16;
  const CELL = 4;

  const DIGIT_MAP = {
    0: ['111111', '111111', '110011', '110011', '110011', '110011', '110011', '110011', '111111', '111111'],
    1: ['001100', '011100', '111100', '001100', '001100', '001100', '001100', '001100', '111111', '111111'],
    2: ['111111', '111111', '000011', '000011', '111111', '111111', '110000', '110000', '111111', '111111'],
    3: ['111111', '111111', '000011', '000011', '111111', '111111', '000011', '000011', '111111', '111111'],
    4: ['110011', '110011', '110011', '110011', '111111', '111111', '000011', '000011', '000011', '000011'],
    5: ['111111', '111111', '110000', '110000', '111111', '111111', '000011', '000011', '111111', '111111'],
    6: ['111111', '111111', '110000', '110000', '111111', '111111', '110011', '110011', '111111', '111111'],
    7: ['111111', '111111', '000011', '000011', '000011', '000011', '000011', '000011', '000011', '000011'],
    8: ['111111', '111111', '110011', '110011', '111111', '111111', '110011', '110011', '111111', '111111'],
    9: ['111111', '111111', '110011', '110011', '111111', '111111', '000011', '000011', '111111', '111111']
  };

  const PLATFORM_ICONS = {
    youtube: ['000111111110', '001111111111', '001111211111', '001111221111', '001111222111', '001111221111', '001111211111', '001111111111', '000111111110', '000000000000'],
    instagram: ['000333333330', '003322222233', '003233333223', '003233223323', '003232332323', '003232332323', '003233223323', '003233333323', '003322222233', '000333333330'],
    x: ['002200000002', '002020000020', '000202000200', '000020202000', '000002020000', '000000202000', '000002020200', '000020002020', '000200000202', '002000000022'],
    tiktok: ['000000123000', '000000122300', '000000122210', '000000123223', '000111123033', '001122123000', '001200123000', '001200123000', '001222223000', '000122330000'],
    facebook: ['011111111110', '111111111111', '111111221111', '111112111111', '111112111111', '111122221111', '111112111111', '111112111111', '111111111111', '011111111110']
  };

  const PLATFORM_COLOR = {
    youtube: '#e71f19',
    instagram: '#e50e7d',
    tiktok: '#00f5ff',
    facebook: '#0068ff',
    x: '#f4f4f2'
  };

  const PLATFORM_ACCENT = {
    instagram: '#e50e7d',
    tiktok: '#eb028b'
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parseCount = (value) => {
    const n = parseInt(String(value || '').replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const formatCount = (num) => {
    try {
      return Math.round(num).toLocaleString('en-US');
    } catch (e) {
      return String(Math.round(num));
    }
  };

  const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);

  const initHub = (root) => {
    if (!root) return;
    if (typeof root._smhHubCleanup === 'function') root._smhHubCleanup();
    root.setAttribute(HUB_READY, '');

    const board = root.querySelector('[data-smh-board]');
    const status = root.querySelector('[data-smh-status]');
    const tabs = Array.from(root.querySelectorAll('[data-smh-tab]'));
    const ctx = board && board.getContext ? board.getContext('2d') : null;
    if (!ctx || !tabs.length) return;

    const interval = Math.max(2000, parseInt(root.getAttribute('data-smh-interval') || '4000', 10) || 4000);
    const duration = Math.max(400, parseInt(root.getAttribute('data-smh-duration') || '900', 10) || 900);

    let index = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
    if (index < 0) index = 0;

    let currentValue = parseCount(tabs[index].getAttribute('data-smh-count'));
    let currentPlatform = tabs[index].getAttribute('data-smh-platform') || 'youtube';
    let currentIcon = null;
    let rafId = 0;
    let timerId = 0;
    let paused = false;
    const iconCache = new Map();

    const loadIcon = (url) => {
      if (!url) return Promise.resolve(null);
      const cached = iconCache.get(url);
      if (cached instanceof Promise) return cached;
      if (cached) return Promise.resolve(cached);
      const pending = new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
          iconCache.set(url, img);
          resolve(img);
        };
        img.onerror = () => {
          iconCache.delete(url);
          resolve(null);
        };
        img.src = url;
      });
      iconCache.set(url, pending);
      return pending;
    };

    const iconFromTab = (tab) => {
      const url = tab && tab.getAttribute('data-smh-icon');
      const cached = url && iconCache.get(url);
      return cached && !(cached instanceof Promise) ? cached : null;
    };

    const drawFollowerCount = (value, platform, iconImg, waitCustom) => {
      const color = PLATFORM_COLOR[platform] || PLATFORM_COLOR.youtube;
      ctx.clearRect(0, 0, board.width, board.height);
      ctx.fillStyle = '#101010';
      ctx.fillRect(0, 0, board.width, board.height);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.025)';
      for (let row = 0; row < ROWS; row += 1) {
        for (let column = 0; column < COLS; column += 1) {
          ctx.fillRect(column * CELL + 1, row * CELL + 1, CELL - 2, CELL - 2);
        }
      }

      const text = String(Math.max(0, Math.round(value))).padStart(5, '0').slice(-5);
      const glyphWidth = 6;
      const glyphHeight = 10;
      const glyphGap = 1;
      const iconPattern = PLATFORM_ICONS[platform] || PLATFORM_ICONS.youtube;
      const useCustomSlot = Boolean(iconImg || waitCustom);
      const iconCols = useCustomSlot ? 10 : iconPattern[0].length;
      const iconRows = useCustomSlot ? 10 : iconPattern.length;
      const iconGap = 2;
      const iconStartRow = Math.floor((ROWS - iconRows) / 2);
      const numberStartRow = Math.floor((ROWS - glyphHeight) / 2);

      if (iconImg) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(
          iconImg,
          1,
          iconStartRow * CELL + 1,
          iconCols * CELL - 1,
          iconRows * CELL - 1
        );
      } else if (!waitCustom) {
        iconPattern.forEach((rowPattern, rowIndex) => {
          Array.from(rowPattern).forEach((pixel, columnIndex) => {
            if (pixel === '0') return;
            ctx.fillStyle =
              pixel === '2'
                ? '#f4f4f2'
                : pixel === '3'
                  ? PLATFORM_ACCENT[platform] || color
                  : color;
            ctx.fillRect(
              columnIndex * CELL + 1,
              (iconStartRow + rowIndex) * CELL + 1,
              CELL - 1,
              CELL - 1
            );
          });
        });
      }

      ctx.fillStyle = '#f4f4f2';
      Array.from(text).forEach((digit, digitIndex) => {
        const glyph = DIGIT_MAP[digit];
        if (!glyph) return;
        glyph.forEach((rowPattern, rowIndex) => {
          Array.from(rowPattern).forEach((pixel, columnIndex) => {
            if (pixel !== '1') return;
            const column = iconCols + iconGap + digitIndex * (glyphWidth + glyphGap) + columnIndex;
            ctx.fillRect(
              column * CELL + 1,
              (numberStartRow + rowIndex) * CELL + 1,
              CELL - 1,
              CELL - 1
            );
          });
        });
      });
    };

    const setTabState = (nextIndex) => {
      tabs.forEach((tab, i) => {
        const selected = i === nextIndex;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
    };

    const show = (nextIndex, { animate = true, focusTab = false } = {}) => {
      const tab = tabs[nextIndex];
      if (!tab) return;

      const nextValue = parseCount(tab.getAttribute('data-smh-count'));
      const nextPlatform = tab.getAttribute('data-smh-platform') || 'youtube';
      const nextIcon = iconFromTab(tab);
      const name = tab.getAttribute('data-smh-name') || 'Social platform';
      const iconUrl = tab.getAttribute('data-smh-icon');
      if (iconUrl && !nextIcon) {
        loadIcon(iconUrl).then((img) => {
          if (!img || tabs[index] !== tab) return;
          currentIcon = img;
          drawFollowerCount(currentValue, currentPlatform, currentIcon, false);
        });
      }

      index = nextIndex;
      setTabState(index);
      if (focusTab) tab.focus();

      if (status) status.textContent = `${name}: ${formatCount(nextValue)} followers`;
      board.setAttribute('aria-label', `${name} followers: ${formatCount(nextValue)}`);

      if (rafId) cancelAnimationFrame(rafId);

      if (!animate || prefersReducedMotion() || nextValue === currentValue) {
        currentValue = nextValue;
        currentPlatform = nextPlatform;
        currentIcon = nextIcon;
        drawFollowerCount(currentValue, currentPlatform, currentIcon, Boolean(iconUrl && !nextIcon));
        return;
      }

      const startValue = currentValue;
      const startPlatform = currentPlatform;
      const startIcon = currentIcon;
      const startTime = performance.now();
      const animDuration = duration;

      const tick = (now) => {
        const progress = Math.min((now - startTime) / animDuration, 1);
        const eased = easeOutCubic(progress);
        const displayValue = Math.round(startValue + (nextValue - startValue) * eased);
        drawFollowerCount(
          displayValue,
          progress < 0.5 ? startPlatform : nextPlatform,
          progress < 0.5 ? startIcon : nextIcon,
          progress >= 0.5 && Boolean(iconUrl && !nextIcon)
        );
        if (progress < 1) {
          rafId = requestAnimationFrame(tick);
        } else {
          currentValue = nextValue;
          currentPlatform = nextPlatform;
          currentIcon = nextIcon;
          rafId = 0;
        }
      };

      rafId = requestAnimationFrame(tick);
    };

    const stopTimer = () => {
      if (timerId) {
        clearInterval(timerId);
        timerId = 0;
      }
    };

    const startTimer = () => {
      stopTimer();
      if (tabs.length < 2 || paused) return;
      timerId = window.setInterval(() => {
        if (paused) return;
        show((index + 1) % tabs.length);
      }, interval);
    };

    const pause = () => {
      paused = true;
      stopTimer();
    };

    const resume = () => {
      paused = false;
      startTimer();
    };

    const onClick = (event) => {
      const i = tabs.indexOf(event.currentTarget);
      if (i < 0 || i === index) return;
      show(i);
      pause();
      window.setTimeout(() => {
        if (!root.matches(':hover') && !root.contains(document.activeElement)) resume();
      }, interval);
    };

    const onKeydown = (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const dir = event.key === 'ArrowRight' ? 1 : -1;
      const next = (index + dir + tabs.length) % tabs.length;
      show(next, { focusTab: true });
      pause();
    };

    const onFocusOut = (event) => {
      if (!root.contains(event.relatedTarget)) resume();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', onClick);
      tab.addEventListener('keydown', onKeydown);
    });

    root.addEventListener('pointerenter', pause);
    root.addEventListener('pointerleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', onFocusOut);

    root._smhHubCleanup = () => {
      stopTimer();
      if (rafId) cancelAnimationFrame(rafId);
      tabs.forEach((tab) => {
        tab.removeEventListener('click', onClick);
        tab.removeEventListener('keydown', onKeydown);
      });
      root.removeEventListener('pointerenter', pause);
      root.removeEventListener('pointerleave', resume);
      root.removeEventListener('focusin', pause);
      root.removeEventListener('focusout', onFocusOut);
      root._smhHubCleanup = null;
      root.removeAttribute(HUB_READY);
    };

    tabs.forEach((tab) => {
      const url = tab.getAttribute('data-smh-icon');
      if (url) loadIcon(url);
    });

    show(index, { animate: false });
    startTimer();
  };

  const destroyMotion = (root) => {
    if (!root) return;
    if (root._smhRetry) {
      clearTimeout(root._smhRetry);
      root._smhRetry = null;
    }
    if (root._smhCtx) {
      root._smhCtx.revert();
      root._smhCtx = null;
    }
    root.removeAttribute(MOTION_READY);
  };

  const setupMotion = (root, attempt) => {
    if (!root || root.hasAttribute(MOTION_READY)) return;
    if (root.getAttribute('data-smh-scroll-motion') !== 'true') return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      if ((attempt || 0) < RETRY_MAX) {
        root._smhRetry = window.setTimeout(() => setupMotion(root, (attempt || 0) + 1), RETRY_MS);
      }
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    root.setAttribute(MOTION_READY, '');

    const display = root.querySelector('[data-smh-display]');
    const copyItems = root.querySelectorAll('[data-smh-copy-item]');
    if (!display && !copyItems.length) return;

    const reduced = prefersReducedMotion();
    const desktop = window.matchMedia('(min-width: 750px)').matches;

    root._smhCtx = gsap.context(() => {
      if (display) {
        gsap.set(display, {
          transformOrigin: desktop ? '85% 50%' : '50% 50%',
          force3D: true
        });
      }
      if (copyItems.length) {
        gsap.set(copyItems, { opacity: 0, y: reduced ? 0 : 28 });
      }

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: root,
          start: 'top 78%',
          end: 'top 36%',
          scrub: reduced ? false : 0.85,
          once: reduced,
          invalidateOnRefresh: true
        }
      });

      if (display) {
        if (reduced) {
          tl.fromTo(display, { opacity: 0 }, { opacity: 1, duration: 0.5 }, 0);
        } else if (desktop) {
          tl.fromTo(
            display,
            { scale: 1.22, xPercent: -10, opacity: 0.35 },
            { scale: 1, xPercent: 0, opacity: 1, duration: 1 },
            0
          );
        } else {
          tl.fromTo(
            display,
            { scale: 1.08, y: 24, opacity: 0.35 },
            { scale: 1, y: 0, opacity: 1, duration: 1 },
            0
          );
        }
      }

      if (copyItems.length) {
        tl.to(
          copyItems,
          {
            opacity: 1,
            y: 0,
            duration: reduced ? 0.4 : 0.55,
            stagger: reduced ? 0 : 0.12,
            ease: reduced ? 'none' : 'power2.out'
          },
          reduced ? 0 : 0.2
        );
      }
    }, root);
  };

  const scan = () => {
    document.querySelectorAll(`[${ROOT_ATTR}]`).forEach((root) => {
      initHub(root);
      setupMotion(root, 0);
    });
  };

  const rescan = () => {
    document.querySelectorAll(`[${ROOT_ATTR}]`).forEach(destroyMotion);
    document.querySelectorAll(`[${ROOT_ATTR}]`).forEach((root) => {
      if (typeof root._smhHubCleanup === 'function') root._smhHubCleanup();
      root.removeAttribute(HUB_READY);
    });
    scan();
    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', rescan);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector(`[${ROOT_ATTR}]`);
    if (!root) return;
    destroyMotion(root);
    if (typeof root._smhHubCleanup === 'function') root._smhHubCleanup();
  });
})();
